import i18next from 'i18next';
import { isArray, isEmpty, isObject, isString } from 'lodash';
import numeral from 'numeral';

import alerts from 'common/modules/alerts';
import showToast from 'common/utils/showToast';

const dataImageRegex =
    /^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i;

const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

export const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const dateRegex =
    /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/i;

export const locationRegex =
    /^([-+]?[0-9]*\.?[0-9]+),\s*([-+]?[0-9]*\.?[0-9]+)$/;

export const arraytoDictionary = (array, key) => {
    if (!Array.isArray(array)) return {};
    return array.reduce(
        (acc, current) => ({ ...acc, [current[key]]: current }),
        {},
    );
};

export const dictionaryToArray = (dictionary) =>
    Object.keys(dictionary).map((key) => dictionary[key]);

export const showReponseErrorsAsAlert = (dispatch, response) => {
    let errors = [];
    if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
        if (response.data.errors) {
            if (isArray(response.data.errors)) {
                errors = response.data.errors.map((error) => `${error}`);
            } else if (isString(response.data.errors)) {
                errors = response.data.errors.split();
            } else if (isObject(response.data.errors)) {
                Object.keys(response.data.errors).forEach((key) => {
                    if (isArray(response.data.errors[key])) {
                        errors = [
                            ...errors,
                            `${key} ${response.data.errors[key][0]}`,
                        ];
                    }
                    if (isString(response.data.errors[key])) {
                        errors = [...errors, response.data.errors[key]];
                    }
                });
            }
        }
    }
    if (errors.length > 0) {
        const dataAlert = {
            confirmText: i18next.t('Accept'),
            messages: errors,
            title: i18next.t('Error'),
            type: alerts.ALERT_TYPE_ALERT,
            variant: 'warning',
        };
        dispatch(alerts.actions.show(dataAlert));
    } else {
        showToast({
            type: 'danger',
            body: i18next.t('An error occurred during the process'),
        });
    }
};

export const formatNumber = ({
    format = '0,0',
    input,
    suffix = '',
    unit = '',
}) =>
    !input
        ? suffix + '0'
        : `${suffix}${numeral(input).format(format, (n) => n)}${unit}`;

export const getDataFromGeocode = (results, type) => {
    if (results) {
        let data = {};
        results.forEach((item) => {
            if (item.types.includes(type)) data = item;
        });
        return data;
    }
    return { formatted_address: '' };
};

export const getHiddenColumns = (tableSettings) =>
    tableSettings?.filter((item) => !item.is_active).map(({ name }) => name) ||
    [];

export const getOrderedColumns = (tableSettings) =>
    tableSettings?.reduce(
        (acc, col) => ({ ...acc, [col.name]: col.order }),
        {},
    ) || {};

export const getPhoneCodeOptions = (codes) => {
    let data = [];
    if (!isEmpty(codes)) {
        data = Object.keys(codes).map((key) => ({
            label: `(${key}) ${codes[key]}`,
            value: codes[key],
        }));
    }
    return [
        {
            value: '',
            label: i18next.t('Select'),
            disabled: true,
        },
        ...data,
    ];
};

export const getPolygonCoordinates = (fieldSegmentPoints) => {
    if (!isArray(fieldSegmentPoints)) return [];
    return fieldSegmentPoints.map((point) => [
        point.y_coordinate,
        point.x_coordinate,
    ]);
};

export const getProductBrandName = ({ brand_name }) =>
    brand_name || i18next.t('No brand');

export const getNewSolarModules = (solarModules) => {
    if (!isArray(solarModules)) return [];
    return solarModules.map((solarModule) => {
        const path = JSON.parse(solarModule.solar_modules).map((point) => [
            point.y,
            point.x,
        ]);
        return {
            cell: solarModule.cell,
            col: solarModule.col,
            enabled: solarModule.is_enable,
            group: solarModule.group,
            id: solarModule.id,
            path,
            row: solarModule.row,
        };
    });
};

export const getSegmentArea = (google, polygon) => {
    if (!polygon.length) return 0;
    return google.maps.geometry.spherical.computeArea(
        polygon.map((coords) => new google.maps.LatLng(coords[0], coords[1])),
    );
};

export const getSegmentDiameter = (google, polygon) => {
    if (!polygon.length) return 0;

    let sumLat = 0;
    let sumLng = 0;
    polygon.forEach((point) => {
        sumLat += point[0];
        sumLng += point[1];
    });
    const center = {
        lat: sumLat / polygon.length,
        lng: sumLng / polygon.length,
    };

    const centerLatLng = new google.maps.LatLng(center.lat, center.lng);
    let totalDistance = 0;

    polygon.forEach((point) => {
        const pointLatLng = new google.maps.LatLng(point[0], point[1]);
        totalDistance += google.maps.geometry.spherical.computeDistanceBetween(
            centerLatLng,
            pointLatLng,
        );
    });

    const radius = totalDistance / polygon.length;
    return parseFloat((radius * 2).toFixed(2));
};

export const getStatusProposalLabel = (isGenerated) => {
    if (isGenerated) return i18next.t('Generated', { context: 'female' });
    return i18next.t('Draft');
};

export const handleFileURL = (url, preffix) => {
    const pattern = /^((https):\/\/)/;
    return pattern.test(url) ? url : `${preffix}${url}`;
};

export const isImageBase64 = (string) => string.match(dataImageRegex);

export const isImageUrl = (string) => string.match(urlRegex);

export const capitalize = (s) => {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export const capitalizeText = (text) =>
    text
        .split(' ')
        .map((word) => capitalize(word))
        .join(' ');

export const countDecimals = (value) => {
    const newValue = parseExponentialNumber(value);
    let decimalsNumber = 0;
    if (!isNaN(parseFloat(newValue))) {
        const parts = newValue.toString().split('.');
        if (parts[1]) decimalsNumber = parts[1].length;
        return decimalsNumber;
    }
    return decimalsNumber;
};

export const getDecimals = (value) => {
    if (!value) return 0;
    const decimals = countDecimals(value);
    if (decimals <= 1) return 1;
    if (decimals >= 2 && decimals <= 4) return decimals;
    return 4;
};

export const numberFormat = (value, options) => {
    const newValue = parseExponentialNumber(value);
    const {
        currency,
        decimals = getDecimals(value),
        locale = 'en-US',
        unit = '',
        style,
    } = options;
    let formatted = newValue;
    let unitText = '';

    switch (style) {
        case 'currency':
            formatted = new Intl.NumberFormat(locale, {
                style,
                currency,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(newValue);
            break;
        case 'decimal':
            formatted = new Intl.NumberFormat(locale, {
                style,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(newValue);
            unitText = unit;
            break;
        default:
            formatted = newValue;
            break;
    }

    if (isEmpty(unitText)) return formatted;
    return `${formatted} ${unitText}`;
};

export const panelTypeToLabel = (panelType) => {
    const panelTypes = {
        0: i18next.t('Monocrystalline'),
        1: i18next.t('Polycrystalline'),
        2: i18next.t('Thin Film'),
    };

    return panelTypes[panelType];
};

export const parseExponentialNumber = (value) => {
    if (!value) return 0;
    if (!(value.toString().indexOf('e') !== -1)) return value;
    const parts = value.toString().split('e-');
    if (parts[1]) return value.toFixed(parts[1]);
};

const stringToColor = (string) => {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
};

export const stringAvatar = (name) => {
    const nameArray = name.split(' ');
    let newName = '';
    if (nameArray.length === 1) newName = nameArray[0][0];
    else if (nameArray.length >= 2 && nameArray[1][0])
        newName = `${nameArray[0][0]}${nameArray[1][0]}`;
    else newName = nameArray[0][0];
    return {
        sx: { bgcolor: stringToColor(name) },
        children: newName?.toUpperCase(),
    };
};

export const handleBuildTheme = ({ baseDarkPalette, baseLightPalette }) => {
    return {
        darkPalette: baseDarkPalette,
        lightPalette: baseLightPalette,
    };
};

export const handleOnChangeLatLng = ({
    latField = 'latitude',
    lngField = 'longitude',
    setValue,
    value,
}) => {
    const regex = locationRegex;
    const match = value.match(regex);
    if (match) {
        const [, lat, lng] = match;
        setValue(latField, lat);
        setValue(lngField, lng);
    } else {
        setValue(latField, null);
        setValue(lngField, null);
    }
};

export const getProjectStatusOptions = () => {
    return [
        {
            label: i18next.t('In process'),
            primary_color: '#34eb40',
            value: 'in_process',
        },
        {
            label: i18next.t('Cancelled'),
            primary_color: '#eb3434',
            value: 'cancelled',
        },
        {
            label: i18next.t('In review'),
            primary_color: '#ebe234',
            value: 'reviewed',
        },
        {
            label: i18next.t('Completed'),
            primary_color: '#d934eb',
            value: 'completed',
        },
        {
            label: i18next.t('Installed'),
            primary_color: '#346eeb',
            value: 'installed',
        },
    ];
};
